

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaReceipt } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import Projects from "./Projects";
import UserStoriesTable from "./UserStoriesTable";
import TasksTable from "./TasksTable";
import { fetchUserStories } from "./userStoryService";
import "../assets/css/DigixLeads.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";
import "../assets/css/Tasks.css";

/**
 * ProjectManagement module:
 * - Projects tab: existing Projects table
 * - Project Estimation tab: placeholder (UI/backend later)
 * - User Stories tab: select project/subproject -> show UserStoriesTable
 * - Tasks tab: select project/subproject/user story -> show TasksTable
 */
function ProjectManagement({
  projects = [],
  loading = false,
  onAddProjectClick,
  onViewProject,
  onEditProject,
  onFetchProjects
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Default to Tasks first (requested tab order: Tasks -> User Stories -> Projects)
  const [activeTab, setActiveTab] = useState("Tasks");
  const [dashboardProjectStatusPreset, setDashboardProjectStatusPreset] = useState("");
  const [dashboardTaskStatusPreset, setDashboardTaskStatusPreset] = useState("");
  /** Keep table instances mounted after first visit so tab switches do not refetch from scratch. */
  const [everMountedUserStories, setEverMountedUserStories] = useState(false);
  const [everMountedTasks, setEverMountedTasks] = useState(false);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const selectedProject = useMemo(
    () => projects.find((p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectId)) || null,
    [projects, selectedProjectId]
  );
  const selectedProjectName = selectedProject?.Project_Name ?? selectedProject?.project_name ?? selectedProject?.name ?? "";

  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState(""); // "" means project-level
  const selectedSubprojectName = useMemo(() => {
    if (!selectedSubprojectId) return "";
    const sp = subprojects.find((s) => String(s.id) === String(selectedSubprojectId));
    return sp?.name || "";
  }, [selectedSubprojectId, subprojects]);

  const userStoriesKeyRef = useRef(0);
  const [userStoriesRefreshKey, setUserStoriesRefreshKey] = useState(0);

  useEffect(() => {
    const s = location.state;
    if (!s || typeof s !== "object") return;
    let applied = false;
    if (
      s.projectManagementTab === "Projects" ||
      s.projectManagementTab === "Project Estimation" ||
      s.projectManagementTab === "User Stories" ||
      s.projectManagementTab === "Tasks"
    ) {
      setActiveTab(s.projectManagementTab);
      applied = true;
    }
    if (s.resetUserStoryFilters) {
      setSelectedProjectId("");
      setSelectedSubprojectId("");
      applied = true;
    }
    if (s.resetTaskFilters) {
      setSelectedProjectId("");
      setSelectedSubprojectId("");
      setSelectedStoryId("");
      applied = true;
    }
    if (typeof s.dashboardProjectStatusPreset === "string") {
      setDashboardProjectStatusPreset(s.dashboardProjectStatusPreset);
      applied = true;
    }
    if (typeof s.dashboardTaskStatusPreset === "string") {
      setDashboardTaskStatusPreset(s.dashboardTaskStatusPreset);
      applied = true;
    }
    if (applied) {
      navigate(".", { replace: true, state: null });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (activeTab === "User Stories") setEverMountedUserStories(true);
    if (activeTab === "Tasks") setEverMountedTasks(true);
  }, [activeTab]);

  const [userStoriesForTasks, setUserStoriesForTasks] = useState([]);
  const [loadingUserStoriesForTasks, setLoadingUserStoriesForTasks] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState("");
  const selectedStoryTitle = useMemo(() => {
    const st = userStoriesForTasks.find((s) => String(s.id) === String(selectedStoryId));
    return st?.title || "";
  }, [selectedStoryId, userStoriesForTasks]);

  // Default behavior for Tasks tab: keep "All Projects" (selectedProjectId === "")
  // so the Tasks table can load aggregated tasks when story is also set to "All".

  const fetchSubprojectsForProject = async (projectId) => {
    if (!projectId) {
      setSubprojects([]);
      return;
    }

    setLoadingSubprojects(true);
    try {
      // Primary: /sub-projects/project/:id
      let response = await axiosInstance.get(`/sub-projects/project/${projectId}`);
      let raw = response.data;

      let arr = [];
      let primaryParsed = false;
      if (Array.isArray(raw)) {
        arr = raw;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.sub_projects)) {
        arr = raw.sub_projects;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.data)) {
        arr = raw.data;
        primaryParsed = true;
      }

      // Fallback legacy endpoints:
      // Only when primary response shape is not what we expect.
      // If primary returns a valid empty list, we should not trigger the legacy call.
      if (!primaryParsed) {
        try {
          response = await axiosInstance.get(`/projects/${projectId}/sub-projects`);
          raw = response.data;
          arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        } catch {
          // Legacy endpoint may not exist in some deployments; treat as "no subprojects".
          arr = [];
        }
      }

      const mapped = (arr || [])
        .map((sp) => ({
          id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
          name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
        }))
        .filter((x) => x.id != null && String(x.name).trim() !== "");

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setSubprojects(mapped);
    } catch (err) {
      console.error("Error fetching subprojects:", err);
      setSubprojects([]);
    } finally {
      setLoadingSubprojects(false);
    }
  };

  // Helper for aggregations: fetch subprojects for a project without mutating UI state.
  const fetchSubprojectsForProjectIds = async (projectId) => {
    if (!projectId) return [];
    try {
      let response = await axiosInstance.get(`/sub-projects/project/${projectId}`);
      let raw = response.data;

      let arr = [];
      let primaryParsed = false;
      if (Array.isArray(raw)) {
        arr = raw;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.sub_projects)) {
        arr = raw.sub_projects;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.data)) {
        arr = raw.data;
        primaryParsed = true;
      }

      if (!primaryParsed) {
        try {
          response = await axiosInstance.get(`/projects/${projectId}/sub-projects`);
          raw = response.data;
          arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        } catch {
          arr = [];
        }
      }

      const mapped = (arr || [])
        .map((sp) => ({
          id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
          name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
        }))
        .filter((x) => x.id != null && String(x.name).trim() !== "");

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      return mapped;
    } catch (err) {
      console.error("Error fetching subprojects for project ids:", projectId, err);
      return [];
    }
  };

  useEffect(() => {
    // Reset story selection when context changes
    setSelectedSubprojectId("");
    setSelectedStoryId("");
    setUserStoriesForTasks([]);

    if (selectedProjectId) {
      fetchSubprojectsForProject(selectedProjectId);
    } else {
      setSubprojects([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  // Stories index for Tasks tab only — no aggregated story fetch while on Projects / User Stories.
  useEffect(() => {
    if (activeTab !== "Tasks") return;

    const loadStories = async () => {
      setLoadingUserStoriesForTasks(true);
      try {
        setSelectedStoryId(""); // default to "All User Stories"

        const allProjectsSelected = selectedProjectId === "";
        const allSubprojectsSelected = selectedSubprojectId === "";

        const projectIdsToLoad = allProjectsSelected
          ? (projects || [])
              .map((p) => p.Project_Id ?? p.project_id ?? p.id)
              .filter((id) => id != null)
          : [Number(selectedProjectId)].filter((n) => Number.isFinite(n) && n > 0);

        if (!projectIdsToLoad.length) {
          setUserStoriesForTasks([]);
          return;
        }

        const subProjectIdNum = selectedSubprojectId ? Number(selectedSubprojectId) : null;

        const loadForProject = async (pid) => {
          // Specific subproject selected: show only those stories for the project.
          if (!allSubprojectsSelected) {
            if (!Number.isFinite(subProjectIdNum) || subProjectIdNum <= 0) return [];
            const list = await fetchUserStories(Number(pid), subProjectIdNum);
            return list || [];
          }

          // All subprojects: include project-level stories + each subproject's stories.
          const results = [];
          const projectLevelStories = await fetchUserStories(Number(pid), undefined);
          results.push(projectLevelStories || []);

          const subList = await fetchSubprojectsForProjectIds(pid);
          if (subList && subList.length > 0) {
            const subStoriesResults = await Promise.all(
              subList.map(async (sp) => {
                const sid = Number(sp.id);
                if (!Number.isFinite(sid) || sid <= 0) return [];
                const s = await fetchUserStories(Number(pid), sid);
                return s || [];
              })
            );
            for (const arr of subStoriesResults) results.push(arr);
          }

          return results.flat();
        };

        const storiesPerProject = await Promise.all(
          projectIdsToLoad.map((pid) => loadForProject(pid))
        );

        const merged = (storiesPerProject || []).flat();
        const mapped = (merged || [])
          .map((st) => ({
            ...st,
            id: st.User_Story_Id ?? st.user_story_id ?? st.id,
            title: st.Title ?? st.title ?? st.User_Story_Title ?? st.user_story_title ?? "—",
            projectId: st.Project_Id ?? st.project_id ?? st.projectId ?? null,
            subProjectId: st.Sub_Project_Id ?? st.sub_project_id ?? st.subProjectId ?? null
          }))
          .filter((x) => x.id != null);

        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setUserStoriesForTasks(mapped);
      } catch (err) {
        console.error("Error loading stories for tasks:", err);
        setUserStoriesForTasks([]);
        setSelectedStoryId("");
      } finally {
        setLoadingUserStoriesForTasks(false);
      }
    };
    loadStories();
  }, [activeTab, selectedProjectId, selectedSubprojectId, projects]);

  const tabs = useMemo(
    () => ["Tasks", "User Stories", "Projects", "Project Estimation"],
    []
  );

  return (
    <div className="projects-container project-management-container">
      <div className="main-card project-management-main-card">
        <div className="project-management-tabs-row">
          <div className="tabs-container">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                className={`tab-button ${activeTab === t ? "active" : ""}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          {activeTab === "Projects" ? (
            <button
              type="button"
              className="btn-add-lead project-management-generate-receipt-btn"
              onClick={() => navigate("/projects/generate-receipt")}
            >
              <FaReceipt style={{ marginRight: 8 }} aria-hidden />
              Generate Receipt
            </button>
          ) : null}
        </div>

      {activeTab === "Projects" ? (
        <div className="project-management-projects-tab">
          <Projects
            projects={projects}
            loading={loading}
            onAddProjectClick={onAddProjectClick}
            onViewProject={onViewProject}
            onEditProject={onEditProject}
            onFetchProjects={onFetchProjects}
            dashboardProjectStatusPreset={dashboardProjectStatusPreset}
          />
        </div>
      ) : null}

      {activeTab === "Project Estimation" ? (
        <div
          className="project-management-tab-content project-management-project-estimation-tab"
          aria-label="Project estimation"
        />
      ) : null}

      {(everMountedUserStories || activeTab === "User Stories") && (
        <div
          className="project-management-tab-content project-management-userstories-tab"
          style={{ display: activeTab === "User Stories" ? "block" : "none" }}
          aria-hidden={activeTab !== "User Stories"}
        >
          <UserStoriesTable
            key={userStoriesRefreshKey}
            dataActive={activeTab === "User Stories"}
            projectId={selectedProjectId ? Number(selectedProjectId) : null}
            subProjectId={selectedSubprojectId ? Number(selectedSubprojectId) : null}
            projectName={selectedProjectName}
            subProjectName={selectedSubprojectName}
            onRefresh={() => {
              userStoriesKeyRef.current += 1;
              setUserStoriesRefreshKey(userStoriesKeyRef.current);
            }}
            projects={projects}
            subprojects={subprojects}
            loadingSubprojects={loadingSubprojects}
            selectedProjectId={selectedProjectId}
            selectedSubprojectId={selectedSubprojectId}
            onProjectChange={(val) => {
              setSelectedProjectId(val);
              if (!val) setSelectedSubprojectId("");
            }}
            onSubprojectChange={setSelectedSubprojectId}
          />
        </div>
      )}

      {(everMountedTasks || activeTab === "Tasks") && (
        <div
          className="project-management-tab-content project-management-tasks-tab"
          style={{ display: activeTab === "Tasks" ? "block" : "none", paddingTop: 0 }}
          aria-hidden={activeTab !== "Tasks"}
        >
          <TasksTable
            dataActive={activeTab === "Tasks"}
            storyId={selectedStoryId ? Number(selectedStoryId) : null}
            projectId={selectedProjectId ? Number(selectedProjectId) : null}
            subProjectId={selectedSubprojectId ? Number(selectedSubprojectId) : null}
            storyTitle={selectedStoryTitle}
            projectName={selectedProjectName}
            subProjectName={selectedSubprojectName}
            projects={projects}
            subprojects={subprojects}
            loadingSubprojects={loadingSubprojects}
            selectedProjectIdValue={selectedProjectId}
            selectedSubprojectIdValue={selectedSubprojectId}
            onProjectChange={(val) => {
              setSelectedProjectId(val);
              if (!val) setSelectedSubprojectId("");
            }}
            onSubprojectChange={setSelectedSubprojectId}
            selectedStoryIdValue={selectedStoryId}
            onStoryChange={setSelectedStoryId}
            loadingUserStoriesForTasks={loadingUserStoriesForTasks}
            storiesForAll={userStoriesForTasks}
            allStoriesSelected={!selectedStoryId}
            dashboardTaskStatusPreset={dashboardTaskStatusPreset}
          />
        </div>
      )}
      </div>
    </div>
  );
}

export default ProjectManagement;

